'use client'

import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store'
import { useEffect, useState, useRef } from 'react'
import { useRouter, useParams, useSearchParams } from 'next/navigation'
import {
    createLoanQuote,
    getLoanQuoteFromToken,
    checkTokenFromCookie,
    updateQuoteStatus,
    postProjectType,
} from '@/app/endpoints/loanComparatorEndpoints'
import {
    LOAN_STATUS,
    LAST_TOKEN_COOKIE_NAME,
    PROJECT_TYPE_NEW_LOAN,
    PROJECT_TYPE_SWITCHING,
} from '@/app/constants/loanTypes'
import { getCookie, setCookie, removeCookie } from '@/app/utils/client-cookies'
import { buildPathQuoteUpdate } from '@/app/components/loanComparator/LoanComparatorProjectStep'

export default function LoanComparatorPathPage() {
    const router = useRouter()
    const params = useParams()
    const searchParams = useSearchParams()
    // Extract token from params - it's an array for catch-all routes [[...token]]
    const tokenParam = params?.token
    const token = Array.isArray(tokenParam) ? tokenParam[0] : tokenParam

    const { quote, quoteActions, uiActions, borrower, ui } = useLoanComparatorStore()

    const [loading, setLoading] = useState(true)
    // Sans token la redirection ne part jamais : sans ce garde la page tourne indéfiniment sur le loader
    const initFailed = !loading && !quote?.token

    // Prevent double initialization (caused by store rehydration or Fast Refresh)
    const hasInitialized = useRef(false)

    // Réponses de l'étape 1 posées sur la LP (widget quick start) : atterrissage direct à l'étape 2
    const quickStartProject = searchParams.get('project')
    const quickStartBorrowers = searchParams.get('borrowers')
    const isQuickStart =
        (quickStartProject === 'new' || quickStartProject === 'switching') &&
        (quickStartBorrowers === 'alone' || quickStartBorrowers === 'duo')
    const hasAppliedQuickStart = useRef(false)

    // The chat prefill rides on ?chat= until the token URL, which the wizard then rewrites
    const chatParam = searchParams.get('chat')
    const chatQuery = (joiner: '?' | '&') => (chatParam ? `${joiner}chat=${encodeURIComponent(chatParam)}` : '')

    // Même séquence que handleProjectNext du wizard : POST projet → refresh serveur → réponses ré-appliquées.
    // Le refresh est indispensable : les formulaires du devis s'initialisent sur le store au premier render
    async function applyQuickStart(quoteToken: string) {
        try {
            const projectTypeValue =
                quickStartProject === 'switching' ? PROJECT_TYPE_SWITCHING : PROJECT_TYPE_NEW_LOAN
            await postProjectType(quoteToken, projectTypeValue, false)

            const refreshedQuote = await getLoanQuoteFromToken(quoteToken)
            quoteActions.setQuote(refreshedQuote)
            quoteActions.update(buildPathQuoteUpdate(quickStartProject === 'switching' ? 'switching' : 'new'))

            if (quickStartBorrowers === 'duo') {
                borrower.addCoBorrower()
            } else {
                borrower.deactivateCoBorrower()
            }

            // URL canonique step-2 obligatoire : la redirection des segments numériques nus perd les query params
            router.replace(
                `/devis-assurance-pret-immobilier/${quoteToken}/step-2?borrowers=${quickStartBorrowers}${chatQuery('&')}`
            )
        } catch (error) {
            console.error('Error applying quick start answers:', error)
            router.replace(`/devis-assurance-pret-immobilier/${quoteToken}/step-1${chatQuery('?')}`)
        }
    }

    async function initQuote() {
        try {
            // If the wrapper has already loaded the quote on the server side, reuse it
            if (quote?.token) {
                setCookie(LAST_TOKEN_COOKIE_NAME, quote.token, 30)
                setLoading(false)
                return
            }

            // Try loading from URL token
            if (token) {
                try {
                    await loadQuote(token)
                    return
                } catch {
                    // Token invalid — remove cookie and fall through to create new quote
                    removeCookie(LAST_TOKEN_COOKIE_NAME)
                }
            }

            // Try recovering from cookie
            const storedToken = getCookie(LAST_TOKEN_COOKIE_NAME)
            if (storedToken && storedToken !== 'undefined') {
                const isValid = await checkTokenFromCookie(storedToken)
                if (isValid) {
                    try {
                        await loadQuote(storedToken)
                        return
                    } catch {
                        removeCookie(LAST_TOKEN_COOKIE_NAME)
                    }
                } else {
                    removeCookie(LAST_TOKEN_COOKIE_NAME)
                }
            }

            // Fallback: always create a new quote (matches Symfony behavior)
            await createQuote()
        } catch (error) {
            console.error('Error initializing quote:', error)
            uiActions.setError('quote', 'Failed to initialize quote')
        } finally {
            setLoading(false)
        }
    }

    const loadQuote = async (tokenToLoad: string) => {
        try {
            const loadedQuote = await getLoanQuoteFromToken(tokenToLoad)
            quoteActions.setQuote(loadedQuote)
            finishQuoteLoading(tokenToLoad)
        } catch (error) {
            console.error('Error loading quote:', error)
            throw error
        }
    }

    const createQuote = async () => {
        try {
            const newQuote = await createLoanQuote()
            quoteActions.setQuote(newQuote)
            setCookie(LAST_TOKEN_COOKIE_NAME, newQuote.token, 30)
            finishQuoteLoading(newQuote.token)
        } catch (error) {
            console.error('Error creating quote:', error)
            throw error
        }
    }

    const finishQuoteLoading = async (quoteToken: string) => {
        try {
            await updateQuoteStatus(quoteToken, LOAN_STATUS.PROJECT_STEP_VISITED)
        } catch (error) {
            console.error('Error updating quote status:', error)
        }
    }

    useEffect(() => {
        if (hasInitialized.current) {
            return
        }
        hasInitialized.current = true

        initQuote()
    }, [])

    // Le choix de projet vit désormais dans l'étape 1 du wizard devis : cette page ne fait
    // plus que préparer le quote (store/token/cookie/création) puis rediriger
    useEffect(() => {
        if (loading || !quote?.token) return

        if (isQuickStart) {
            if (hasAppliedQuickStart.current) return
            hasAppliedQuickStart.current = true
            applyQuickStart(quote.token)
            return
        }

        router.replace(`/devis-assurance-pret-immobilier/${quote.token}/step-1${chatQuery('?')}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, quote?.token, router])

    if (initFailed) {
        return (
            <div className="min-h-[calc(100vh-80px)] bg-neutral flex items-center justify-center px-6">
                <div className="max-w-[520px] text-center">
                    <p className="mb-2 text-lg font-semibold text-pale">
                        Nous n&apos;avons pas pu d&eacute;marrer votre simulation
                    </p>
                    <p className="mb-6 text-gray-600">
                        {ui.errors.quote
                            ? 'Une erreur est survenue de notre côté. Merci de réessayer dans un instant.'
                            : 'Merci de réessayer dans un instant.'}
                    </p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="inline-flex items-center gap-2.5 rounded bg-rm-blue px-5 py-[15px] text-[14px] text-white transition-opacity hover:opacity-90"
                    >
                        R&eacute;essayer
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-[calc(100vh-80px)] bg-neutral flex items-center justify-center">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement...</p>
            </div>
        </div>
    )
}
